import React from 'react';
import './DemoAnimationOverlay.css';

interface CursorPosition {
    readonly x: number; // 0–100 %
    readonly y: number; // 0–100 %
}

interface DemoAnimationOverlayProps {
    /** Cursor position as percentage of the containing element */
    readonly cursorPos: CursorPosition;
    /** CSS transition duration in ms; pass 0 to teleport instantly */
    readonly cursorTransitionMs: number;
    /** Triggers the press animation + ripple */
    readonly isClicking: boolean;
    /** Controls the overlay opacity (fade in/out) */
    readonly isVisible: boolean;
    /** Popup panels / annotations rendered inside the overlay */
    readonly children?: React.ReactNode;
}

/**
 * Reusable animated-cursor overlay.
 * Place this as a direct child of a `position: relative` container.
 * The cursor is absolutely positioned via x/y percentages so it
 * works correctly regardless of the container's pixel dimensions.
 */
const DemoAnimationOverlay = ({
    cursorPos,
    cursorTransitionMs,
    isClicking,
    isVisible,
    children,
}: DemoAnimationOverlayProps): React.ReactElement => (
    <div
        className='dao-overlay'
        aria-hidden='true'
        style={{ opacity: isVisible ? 1 : 0 }}
    >
        {children}

        <div
            className={`dao-cursor${isClicking ? ' dao-cursor--click' : ''}`}
            style={{
                left: `${cursorPos.x}%`,
                top: `${cursorPos.y}%`,
                transition:
                    cursorTransitionMs > 0
                        ? `left ${cursorTransitionMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
                           top  ${cursorTransitionMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
                        : 'none',
            }}
        >
            <svg
                className='dao-cursor-svg'
                viewBox='0 0 14 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M0.5 0.5L0.5 16.5L4.5 12.5L7.5 18.5L9.5 17.5L6.5 11.5H12.5L0.5 0.5Z'
                    fill='white'
                    stroke='#1a1a2e'
                    strokeWidth='1.2'
                    strokeLinejoin='round'
                    strokeLinecap='round'
                />
            </svg>

            {isClicking && <div className='dao-click-ripple' />}
        </div>
    </div>
);

export { DemoAnimationOverlay };
export type { CursorPosition };
